// src/pages/club/settings/subscription/ModalClubConfiguration.jsx
import "./ModalClubConfiguration.scss";

import { Modal, ProgressBar } from "react-bootstrap";
import { useEffect, useMemo, useState } from "react";

import AppLoading from "../../../../commons/AppLoading";
import ClubPlanAPI from "../api/ClubPlanAPI";
import ClubStep1 from "./steps/ClubStep1";
import ClubStep2 from "./steps/ClubStep2";
import ClubStep3 from "./steps/ClubStep3";
import ClubStep4 from "./steps/ClubStep4";
import HallAppToast from "../../../../commons/HallAppToast";

const clubPlanAPI = new ClubPlanAPI();

function StepDots({ step, total }) {
  return (
    <div className="d-flex align-items-center gap-2 w-100">
      {Array.from({ length: total }).map((_, idx) => {
        const i = idx + 1;
        const active = i <= step;

        return (
          <div key={i} className="d-flex align-items-center flex-grow-1">
            <div
              style={{
                width: 12,
                height: 12,
                borderRadius: 999,
                background: active ? "#2f2a7a" : "#e7e7f4",
                border: active ? "1px solid #2f2a7a" : "1px solid #d8d8ee",
              }}
              aria-label={`Passo ${i}`}
            />
            {i !== total && (
              <div
                style={{
                  height: 2,
                  flex: 1,
                  marginLeft: 8,
                  marginRight: 8,
                  background: i < step ? "#2f2a7a" : "#e7e7f4",
                }}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

function toWizardData(plan) {
  if (!plan) return {};
  const rules = plan.rules || {};

  // backward compat: se existir maxRedemptionsPerCycle antigo e não existir redemptionLimit novo,
  // traz isso como period=CYCLE
  const hasNewRedemption =
    rules?.redemptionLimitQty != null || rules?.redemptionLimitPeriod != null;

  const legacyCycleQty = rules?.maxRedemptionsPerCycle != null ? Number(rules.maxRedemptionsPerCycle) : null;

  const redemptionLimitQty =
    hasNewRedemption ? rules?.redemptionLimitQty : legacyCycleQty;

  const redemptionLimitPeriod =
    hasNewRedemption ? rules?.redemptionLimitPeriod : legacyCycleQty ? "CYCLE" : "MONTH";

  return {
    id: plan.id,

    // Step 1
    name: plan.name ?? "",
    shortDescription: plan.shortDescription ?? "",
    fullDescription: plan.fullDescription ?? "",
    amount: plan.amount ?? 0,
    currency: plan.currency ?? "BRL",
    frequency: plan.frequency ?? "MONTHLY",
    billingRule: plan.billingRule ?? "ON_SUBSCRIPTION_DAY",
    fixedDayOfMonth: plan.fixedDayOfMonth ?? "",
    limitSubscribers: plan.limitSubscribers ?? null,
    allowMultipleSubscriptionsPerCustomer: !!plan.allowMultipleSubscriptionsPerCustomer,
    visibleOnSite: !!plan.visibleOnSite,
    active: plan.active !== false,
    displayOrder: plan.displayOrder ?? null,
    salesChannels: Array.isArray(plan.salesChannels) ? plan.salesChannels : [],

    // Step 2
    benefits: Array.isArray(plan.benefits) ? plan.benefits : [],

    // Step 3
    rules: {
      ...rules,
      redemptionLimitQty: redemptionLimitQty ?? null,
      redemptionLimitPeriod: redemptionLimitPeriod ?? "MONTH",
    },
  };
}

function ModalClubConfiguration({ show, closeShow, clubPlanId, onSuccess }) {
  const TOTAL_STEPS = 4;
  const editing = !!clubPlanId;

  const [step, setStep] = useState(1);
  const [data, setData] = useState({});
  const [loadingPlan, setLoadingPlan] = useState(false);

  const progress = useMemo(() => Math.round((step / TOTAL_STEPS) * 100), [step]);

  const stepTitle = useMemo(() => {
    switch (step) {
      case 1:
        return "Plano";
      case 2:
        return "Benefícios";
      case 3:
        return "Regras";
      case 4:
        return "Revisão";
      default:
        return "Clube de Assinatura";
    }
  }, [step]);

  const next = (value) => {
    setData((prev) => ({ ...prev, ...value }));
    setStep((prev) => prev + 1);
  };

  const back = () => setStep((prev) => prev - 1);

  const loadPlan = async () => {
    if (!clubPlanId) return;

    try {
      setLoadingPlan(true);

      const res = await clubPlanAPI.getById(clubPlanId);
      const plan = res?.data || res;

      setData(toWizardData(plan));
      setStep(1);
    } catch (e) {
      HallAppToast.error("Não foi possível carregar o clube para edição.");
      closeShow?.();
    } finally {
      setLoadingPlan(false);
    }
  };

  useEffect(() => {
    if (!show) return;

    setStep(1);

    if (editing) {
      loadPlan();
      return;
    }

    setData({});
    setLoadingPlan(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, clubPlanId]);

  const handleSubmit = async (payload) => {
    if (editing) return await clubPlanAPI.update(clubPlanId, payload);
    return await clubPlanAPI.create(payload);
  };

  return (
    <Modal
      show={show}
      fullscreen={true}
      centered
      size="lg"
      onHide={() => closeShow?.()}
      contentClassName="hallapp-modal"
    >
      <Modal.Header closeButton className="border-bottom">
        <div className="w-100">
          <div className="d-flex align-items-start justify-content-between gap-2">
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 16, fontWeight: 800, lineHeight: "20px" }}>
                {editing ? "Editar Clube" : "Criar Clube"}
              </div>
              <div className="text-muted" style={{ fontSize: 12 }}>
                {stepTitle} • Passo {step} de {TOTAL_STEPS}
              </div>
            </div>
          </div>

          <div className="mt-3">
            <StepDots step={step} total={TOTAL_STEPS} />
            <div className="mt-2">
              <ProgressBar now={progress} style={{ height: 6, borderRadius: 999 }} />
            </div>
          </div>
        </div>
      </Modal.Header>

      <Modal.Body>
        {loadingPlan ? (
          <AppLoading />
        ) : (
          <div className="container">
            {step === 1 && <ClubStep1 nextStep={next} data={data} />}
            {step === 2 && <ClubStep2 nextStep={next} toBack={back} data={data} />}
            {step === 3 && <ClubStep3 nextStep={next} toBack={back} data={data} />}
            {step === 4 && (
              <ClubStep4
                data={data}
                toBack={back}
                closeModal={closeShow}
                onSuccess={onSuccess}
                onSubmit={handleSubmit}
                editing={editing}
                clubPlanId={clubPlanId}
              />
            )}
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
}

export default ModalClubConfiguration;
